/**
 * Detecting a moved product.
 *
 * The API answers a changed slug with a 301 carrying `{ redirectTo }`. What is
 * actually observable from browser `fetch` depends on whether the server sends
 * a `Location` header:
 *
 *   - No Location  -> the browser does NOT follow; we see status 301 and can
 *                     read the body. (Most likely: the doc describes a body,
 *                     and NextResponse.json(..., {status:301}) sets no Location.)
 *   - With Location -> the browser follows transparently; we see the
 *                     destination's 200, but `res.redirected` is true and
 *                     `res.url` is the final URL.
 *
 * `redirect: 'manual'` is the wrong tool: it yields an opaque response
 * (status 0, unreadable body), telling us a redirect happened while destroying
 * the payload we need.
 *
 * Rule 4 below is the universal safety net — whatever the transport does, if
 * the payload's canonical slug differs from what we asked for, the URL is
 * stale. It alone handles the Location case and survives the server dropping
 * the 301 entirely.
 */
import type { ProductBySlugResult, ShopProductDetailResponse } from '../types';

/**
 * `redirectTo` may arrive as a bare slug, a path, or an absolute URL.
 * Returns a safe bare slug, or null if the value can't be trusted.
 */
export const normaliseRedirectTarget = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Open-redirect guard: never follow off-origin targets.
  if (trimmed.includes('://') || trimmed.startsWith('//')) return null;

  const segments = trimmed.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  if (!last) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(last);
  } catch {
    decoded = last;
  }

  // Strip any query/hash that rode along on the segment.
  const clean = decoded.split('?')[0].split('#')[0].trim();
  if (!clean || clean === '.' || clean === '..') return null;

  return clean;
};

/** Extracts the trailing slug from a fully-qualified response URL. */
export const slugFromUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    return last ? decodeURIComponent(last) : null;
  } catch {
    return null;
  }
};

/**
 * Layered resolution — correct whether or not the browser followed a redirect.
 */
export const resolveProductResponse = (
  requestedSlug: string,
  res: Response,
  body: unknown
): ProductBySlugResult => {
  const payload = (body ?? {}) as Partial<ShopProductDetailResponse> & {
    redirectTo?: unknown;
  };

  // 1 & 2. An explicit redirectTo, on a 301/308 or on a 200.
  if (payload.redirectTo !== undefined) {
    const target = normaliseRedirectTarget(payload.redirectTo);
    if (target && target !== requestedSlug) return { kind: 'redirect', slug: target };
  }

  // 3. The browser followed a Location header for us.
  if (res.redirected) {
    const target = slugFromUrl(res.url);
    if (target && target !== requestedSlug) return { kind: 'redirect', slug: target };
  }

  // 4. Safety net: the canonical slug in the payload disagrees with the URL.
  if (payload.product?.slug && payload.product.slug !== requestedSlug) {
    return { kind: 'redirect', slug: payload.product.slug };
  }

  return {
    kind: 'product',
    product: payload.product as ShopProductDetailResponse['product'],
    similar: Array.isArray(payload.similar) ? payload.similar : [],
  };
};
